// @ts-nocheck

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import { useViewportContext } from '../../../../contexts/ViewportContext';
import useModalContext from '../../../../hooks/useModalContext';
import { computeAppBarHeight } from '../../../../utils/layout';
import { BoomAssetsBySkinId } from '../../constants/assets';
import BoomUIBySkinId from '../../constants/ui';
import { useBoomContext } from '../../contexts/BoomContext';
import { BoomViewContext } from '../../contexts/BoomViewContext';
import {
  selectBoomGlobalOperationEndDate,
  selectBoomGlobalStatus,
} from '../../selectors/global/globalPrimary.selector';
import { BoomGlobalStatuses } from '../../types/global';

const Wrapper = styled.div`
  padding: 1em;
  position: relative;
  transform: translateZ(0);
  overflow: hidden;

  ${({ $availableHeight, $availableWidth }) => css`
    width: ${$availableWidth ? `${$availableWidth}px` : '100%'};
    min-height: ${$availableHeight ? `${$availableHeight}px` : '100%'};
    height: ${({ $fixHeight, $availableHeight }) => ($fixHeight ? `${$availableHeight}px` : 'auto')};
  `}

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: -1;

    ${({ $isExtendedShadow, $skinId }) => {
      const wrapperStyle = BoomUIBySkinId[$skinId]?.WRAPPER_STYLE ?? {};

      return $isExtendedShadow
        ? css`
            background-image: ${wrapperStyle.extendedShadowBackgroundImage};
            height: ${wrapperStyle.extendedShadowHeight};
          `
        : css`
            background-image: ${wrapperStyle.shadowBackgroundImage};
            height: ${wrapperStyle.shadowHeight};
          `;
    }}
  }
`;

const Background = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: -1;

  ${({ $skinId }) => css`
    background-color: ${BoomUIBySkinId[$skinId]?.WRAPPER_STYLE.backgroundColor};
    background-image: url(${BoomAssetsBySkinId[$skinId]?.BackgroundImg});
  `}
  background-attachment: fixed;
  background-repeat: no-repeat;

  ${Target.__MOBILE__
    ? css`
        background-size: 290%;
      `
    : css`
        background-size: 200%;
      `}

  filter: var(--BoomViewWrapper-filter, none);

  transition: filter 300ms ease-in-out;
`;

type BoomViewWrapperProps = {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
  noScroll?: boolean;
  isExtendedShadow?: boolean;
  'data-testid'?: string;
};

const BoomViewWrapper = ({
  className = '',
  children,
  onClick,
  noScroll = false,
  isExtendedShadow = false,
  'data-testid': dataTestId,
}: BoomViewWrapperProps) => {
  const { skinId } = useBoomContext();
  const { height, width, ratio, domNodeRef } = useViewportContext();
  const { height: modalHeight, width: modalWidth } = useModalContext();
  const [fixScroll, setFixScroll] = useState(noScroll);
  const globalStatus = useSelector(selectBoomGlobalStatus);
  const operationEndDate = useSelector(selectBoomGlobalOperationEndDate);

  const hasExpiredBand = useMemo(() => {
    let willOperationEndSoon = false;
    if (typeof operationEndDate === 'number') {
      willOperationEndSoon = operationEndDate - Date.now() < 1000 * 3600 * 24;
    }
    return globalStatus === BoomGlobalStatuses.ENDING || willOperationEndSoon;
  }, [globalStatus, operationEndDate]);

  const { navbarHeight, tabbarHeight } = useMemo(
    () => (ratio && domNodeRef && domNodeRef.current ? computeAppBarHeight(ratio, domNodeRef.current) : {}),
    [ratio, domNodeRef],
  );

  const availableHeight = useMemo(() => {
    const calculatedHeight = modalHeight ? modalHeight : height - navbarHeight - tabbarHeight;
    // adjust height if expired band is visible
    return hasExpiredBand ? calculatedHeight - 48 : calculatedHeight;
  }, [hasExpiredBand, height, modalHeight, navbarHeight, tabbarHeight]);

  const availableWidth = useMemo(() => (modalWidth ? modalWidth : width), [modalWidth, width]);

  const providedState = useMemo(
    () => ({
      height: availableHeight,
      width: availableWidth,
      fixScroll,
      setFixScroll,
    }),
    [availableHeight, availableWidth, fixScroll],
  );

  return (
    <Wrapper
      data-testid={dataTestId}
      id="boom-view-wrapper"
      className={className}
      $availableWidth={modalWidth}
      $availableHeight={availableHeight}
      $fixHeight={fixScroll}
      onClick={onClick}
      $isExtendedShadow={isExtendedShadow}
      $skinId={skinId}
    >
      <Background $skinId={skinId} />
      <BoomViewContext.Provider value={providedState}>{children}</BoomViewContext.Provider>
    </Wrapper>
  );
};

export default BoomViewWrapper;
